<template>
    <div class="mt-sm md:mt-lg">
        <products-added-to-cart v-show="productsAddedToCart" />
        <transition name="cart">
            <div v-show="! productsAddedToCart" class="card has-footer">
                <h1 class="font-regular">{{ tvPackage.name }}</h1>
                <div class="text-md text-neutral-dark mb-lg md:mb-5xl lg:max-w-lg">
                    {{ tvPackage.description }}
                </div>

                <div v-if="campaigns.length > 0" class="md:flex md:mb-5xl">
                    <variant
                        v-for="campaign in campaigns"
                        :key="campaign.id"
                        :variant="campaign"
                        :is-campaign="true"
                        :selected-variant="selectedCampaign"
                        :product-type="lang('Kampanj','Campaign')"
                        :product-variation="campaign.name"
                        :price="getCampaignPrice(campaign)"
                        :price-after-campaign="getCampaignAfterPrice(campaign)"
                        @select="setCampaign"
                    />

                    <variant
                        :is-campaign="false"
                        :variant="null"
                        :selected-variant="selectedCampaign"
                        :product-variation="tvPackage.name"
                        :price="format_sek_per_month(tvPackage.monthly_price)"
                        @select="setCustomSettings"
                    />
                </div>

                <setting-display-channels
                    :tv-package="tvPackage"
                />

                <setting-with-text
                    v-if="selectedCampaign"
                    :setting="selectedCampaign.subtitle"
                    :title="lang('Tv-paket som ingår','Included TV-package')"
                />

                <setting-select-flex-channels
                    v-if="tvPackage.is_flex"
                    :channels="tvPackage.flex_channels"
                    :max-flex-channels="8"
                    @change="(newChannels) => selectedFlexChannels = newChannels"
                />

                <setting-with-text
                    :setting="getAttributeFromSelected('binding_period_label')"
                    :title="lang('Bindningstid','Binding period')"
                    :description="lang('Tjänstens bindningstid','The service binding period')"
                />

                <setting-with-text
                    :setting="terminationTimeLabel"
                    :title="lang('Uppsägningstid','Termination time')"
                    :description="lang('Tjänstens uppsägningstid','The service termination time')"
                />

                <setting-with-text
                    v-if="startFee > 0"
                    :setting="startFeeLabel"
                    :title="lang('Startavgift','Initial cost')"
                    :description="lang('Tjänstens startavgift','The service initial cost')"
                />

                <router-selector
                    v-model="selectedRouter"
                    :routers="routers"
                    :configuration-data="configurationData"
                    :allow-none-selection="! isRouterRequired"
                />

                <hd-box-selector
                    v-model="selectedHdBox"
                    :allow-none-selection="! isHdBoxRequired"
                    :has-collective-hd-box="hasCollectiveHdBox"
                    :hd-boxes="selectableHdBoxes"
                    :hard-drive-for-hd-box="configurationData.hardDriveForHdBox"
                    @hard-drive-updated="updateSelectedHardDrive"
                />

                <setting-with-checkbox
                    v-if="streamOptions.length"
                    v-model="selectedStreams"
                    :options="streamOptions"
                    :title="lang('Stream','Stream')"
                    :description="streamDescription"
                />

                <configuration-footer
                    :monthly-price="monthlyPrice"
                    :one-time-cost="oneTimeCost"
                    :validates="true"
                    @add="addToCart"
                />
            </div>
        </transition>
        <products-added-to-cart v-show="addonsAddedToCart" :show-cart-button="false">
            {{ lang('Dina tillval är tillagda i varukorgen','Your addons have been added to the cart') }}
        </products-added-to-cart>
        <tv-popular-addons
            v-show="productsAddedToCart && ! addonsAddedToCart"
            :twin="configurationData.twin"
            :show-twin="showTwin"
            :hard-drive="configurationData.hardDriveForTwin"
            :available-tv-packages="configurationData.availableTvPackages"
            :available-tv-channels="availableTvChannels"
            @completed="addonsAddedToCart = true"
        />
        <slot v-if="productsAddedToCart" />
    </div>
</template>
<script>
    import SettingWithText from '../Settings/SettingWithText';
    import SettingDisplayChannels from '../Settings/DisplayChannelsSetting';
    import SettingSelectFlexChannels from '../Settings/SelectFlexChannelsSetting';
    import SettingWithCheckbox from '../Settings/SettingWithCheckbox';
    import ConfigurationFooter from '../ConfigurationFooter';
    import Variant from '../Variant';
    import BaseConfigurator from '../BaseConfigurator';
    import ProductsAddedToCart from '../ProductsAddedToCart';
    import TvPopularAddons from './TvPopularAddons';
    import { orderBy } from 'lodash-es';
    import StreamMixin from '../../../Shared/StreamMixin';
    import RouterSelector from '../Hardware/RouterSelector.vue';
    import HdBoxSelector from '../Hardware/HdBoxSelector.vue';

    export default {
        components: {
            HdBoxSelector,
            RouterSelector,
            TvPopularAddons,
            SettingWithText,
            SettingDisplayChannels,
            SettingSelectFlexChannels,
            SettingWithCheckbox,
            Variant,
            ConfigurationFooter,
            ProductsAddedToCart,
        },
        extends: BaseConfigurator,
        mixins: [StreamMixin],
        props: {
            configurationData: {
                type: Object,
                required: true,
            },
        },
        data() {
            return {
                tvPackage: this.configurationData.organizationTvPackage,
                routers: this.configurationData.routers,
                hasCollectiveRouter: this.configurationData.hasCollectiveRouter,
                hasCollectiveHdBox: this.configurationData.hasCollectiveHdBox,
                campaigns: this.configurationData.campaigns,
                selectedCampaign: null,
                selectedFlexChannels: null,
                productsAddedToCart: false,
                addonsAddedToCart: false,
                selectedStreams: [],
                selectedHdBox: null,
                selectedRouter: null,
                selectedHardDrive: null,
            };
        },
        computed: {
            showTwin() {
                if (this.chosenTvCampaignIncludesTwin) {
                    return false;
                }

                return !! (this.selectedHdBox || this.configurationData.hasCollectiveHdBox);
            },
            isRouterRequired() {
                if (! this.selectedHdBox) {
                    return false;
                }

                return this.tvPackage && this.tvPackage.requires_router;
            },
            monthlyPrice() {
                if (this.selectedCampaign !== null) {
                    return format_sek_per_month(this.getAttributeFromSelected('discounted_monthly_price') + this.streamMonthlyCost);
                }

                if (this.selectedProduct === null) {
                    return '-';
                }

                if (typeof this.getAttributeFromSelected('monthly_price') === 'number') {
                    return format_sek_per_month(this.getAttributeFromSelected('monthly_price') + this.streamMonthlyCost);
                }

                return '-';
            },
            selectedCampaignHasRouter() {
                return this.selectedCampaign && this.selectedCampaign.routers.length > 0;
            },
            streamOptions() {
                let streamPackages = this.configurationData.streamPackages;
                const selectedStreamPackages = this.selectedStreams;

                if (streamPackages.length > 0) {
                    streamPackages = this.sortAndTransformStreamingPackages(
                        streamPackages
                    );

                    if (streamPackages.filter((streamPack) => streamPack.included).length > 0) {
                        let gaveDiscount = false;
                        streamPackages.map((selectedStreamPackage, index) => {
                            // Only the most expensive one will be free if multiple included
                            // packages are selected
                            if ((selectedStreamPackages.length < 1 || ! gaveDiscount) && selectedStreamPackage.included) {
                                selectedStreamPackage.price = 0;
                                gaveDiscount = true;
                            }

                            if (
                                selectedStreamPackages.length === 1
                                && (
                                    selectedStreamPackage.article_id === selectedStreamPackages[0]
                                    && selectedStreamPackage.included
                                )
                            ) {
                                selectedStreamPackage.price = 0;
                                gaveDiscount = true;
                            }

                            return selectedStreamPackage;
                        });
                    }

                    return streamPackages.map((tvPackage) => {
                        return {
                            value: tvPackage.article_id,
                            label: tvPackage.name,
                            name: 'streams[]',
                            valuePrice: format_sek(tvPackage.price),
                        };
                    });
                }

                return [];
            },
            selectedStreamArticles() {
                return this.configurationData.streamPackages.filter((streamPackage) => {
                    return this.selectedStreams.some((selectedStreamPackageId) => {
                        return streamPackage.article_id === selectedStreamPackageId;
                    });
                });
            },
            chosenTvCampaignIncludesTwin() {
                if (! this.selectedCampaign) {
                    return false;
                }

                return !! this.selectedCampaign.includes_twin;
            },
            oneTimeCost() {
                let oneTimeCost = 0;

                if (this.selectedCampaign) {
                    oneTimeCost += this.selectedCampaign.start_fee;
                }

                if (this.selectedRouter) {
                    oneTimeCost += this.selectedRouter.price;
                }

                if (this.selectedHdBox) {
                    oneTimeCost += this.selectedHdBox.price;
                }

                if (this.selectedHardDrive) {
                    oneTimeCost += this.selectedHardDrive.price;
                }

                return format_sek(oneTimeCost);
            },
            selectableHdBoxes() {
                if (this.hasCollectiveHdBox) {
                    return [];
                }

                if (this.selectedCampaign !== null) {
                    return this.selectedCampaign.hd_boxes;
                }

                return this.configurationData.hdBoxes;
            },
            streamDescription() {
                if (this.configurationData.streamPackages && this.configurationData.streamPackages.filter(stream => stream.is_included).length) {
                    return this.translation('tv_stream_configurator_has_included_description');
                } else {
                    return this.translation('tv_stream_configurator_none_included_description');
                }
            },
            isHdBoxRequired() {
                if (this.hasCollectiveHdBox) {
                    return false;
                }

                if (this.selectedCampaign) {
                    return this.selectedCampaign.hd_box_required;
                }

                return this.tvPackage.hd_box_required;
            },
            availableTvChannels() {
                const channels = this.configurationData.availableTvChannels;

                if (! this.hasCollectiveHdBox && ! this.selectedHdBox) {
                    return channels.filter(channel => ! channel.hd_box_required);
                }

                return channels;
            },
        },
        created() {
            this.trackProductDetailView();
        },
        methods: {
            sortAndTransformStreamingPackages(packages) {
                return orderBy(packages.map((streamPackage) => {
                    return {
                        id: streamPackage.article_id,
                        included: streamPackage.is_included,
                        price: streamPackage.monthly_price,
                        article_id: streamPackage.article_id,
                        article_class: streamPackage.article_class,
                        name: streamPackage.name,
                    };
                }), ['price', 'name'], 'desc');
            },
            getAttributeFromSelected(attribute) {
                if (this.selectedCampaign !== null) {
                    return this.selectedCampaign[attribute];
                }

                return this.tvPackage[attribute];
            },
            setCampaign(campaign) {
                this.selectedCampaign = campaign;
            },
            setCustomSettings() {
                this.selectedCampaign = null;
            },
            async addToCart() {
                const mainProduct = {
                    articleClass: this.getAttributeFromSelected('article_class'),
                    articleId: this.getAttributeFromSelected('article_id'),
                    subItems: {},
                };

                mainProduct['parameters'] = {
                    hardDriveSelected: !! this.selectedHardDriver,
                };

                if (this.tvPackage.is_flex) {
                    mainProduct['parameters']['flexChannels'] = this.selectedFlexChannels;
                }

                if (this.selectedHdBox) {
                    mainProduct['subItems']['hdBox'] = {
                        articleClass: this.selectedHdBox.article_class,
                        articleId: this.selectedHdBox.article_id,
                    };
                }

                const articles = [
                    mainProduct,
                ];

                this.selectedStreamArticles.forEach(article => {
                    articles.push({
                        articleId: article.article_id,
                        articleClass: article.article_class,
                    });
                });

                if (this.selectedCampaignHasRouter && this.selectedRouter) {
                    mainProduct['subItems']['router'] = {
                        articleClass: this.selectedRouter.article_class,
                        articleId: this.selectedRouter.article_id,
                    };
                } else if (this.selectedRouter) {
                    articles.push({
                        articleClass: this.selectedRouter.article_class,
                        articleId: this.selectedRouter.article_id,
                    });
                }

                const response = await axios.post(route('front.api.cart-items.store'), {
                      articles,
                });

                EventBus.$emit('cartDataUpdated', response.data);

                this.productsAddedToCart = true;
                this.scrollIt('#popular-addons');
                this.trackTvAddonImpression();
            },
            trackTvAddonImpression() {
                const products = [];

                if (! this.chosenTvCampaignIncludesTwin) {
                    products.push(this.configurationData.twin.gtm_product_data);
                }

                this.configurationData.availableTvPackages.forEach(tvPackage => {
                    products.push(tvPackage.gtm_product_data);
                });

                this.$GtmTracker.productImpressions(products, 'Tillval');
            },
            trackProductDetailView() {
                const products = [];

                products.push(this.tvPackage.gtm_product_data);
                this.campaigns.forEach(campaign => products.push(campaign.gtm_product_data));

                this.configurationData.streamPackages.forEach(streamPackage => {
                    products.push(streamPackage.gtm_product_data);
                });

                this.$GtmTracker.productDetailView(products);
            },
            updateSelectedHardDrive(hardDrive) {
                this.selectedHardDrive = hardDrive;
            },
        },
    };
</script>
